<template>
	<div
		:style="{ 'background-image': backgroundImage, 'background-color': backgroundColor }"
		class="flow-run flex-centerize flex-column"
		:class="{ 'has-mobile-background': hasMobileBackground, 'in-app-mode': inAppMode }"
	>
		<PortalAuth :workflowId="hashId" :requestCodeText="requestCodeText" v-if="isPortalLogin" @authenticated="handlePortalAuthSucceeded" />
		<div v-else class="flow-content">
			<v-img v-if="workflowMetadata.icon" :src="workflowMetadata.icon" class="mx-auto mt-3" width="50" height="50" />
			<div class="flow-title" v-if="workflowMetadata.page_title">{{ workflowMetadata.page_title }}</div>
			<div class="loader-container display-flex flex-column flex-centerize pb-12" v-show="showIntro">
				<AppLoader />
			</div>
			<WidgetForm
				ref="widget_layout"
				v-show="showWidget"
				allowFocusOnFirstElement
				:logoSrc="logoSrc"
				:isLogoActive="isLogoActive"
				:isEndRun="isShowingEndRun"
			/>
		</div>
		<div
			class="workflow-status-wrapper display-flex flex-row flex-align-end flex-justify-space-between width-100 px-3"
			:class="{ 'mobile-margin': isMobile && showLoader, 'mr-5': isMobile && !showLoader }"
		>
			<WorkflowStatus
				ref="workflowStatusRef"
				:userIsOwner="userIsOwner"
				:showSimpleLoader="showLoader"
				:userInOrganization="userInOrganization"
				:class="{ 'pointer-events-none': showDebugger }"
				:hideErrorsFromOrganization="hideErrorsFromOrganization"
				:hideErrorsFromNonOrganization="hideErrorsFromNonOrganization"
				:forceNonExistantToolError="forceNonExistantToolError"
				allowMinimize
			/>
			<div class="display-flex flex-row flex-align-center pb-3">
				<AnemicButton
					v-if="!inAppMode"
					class="powered-by-button"
					href="https://ply.io?utm_source=powered_by_ply&utm_medium=feature"
					target="_blank"
				>
					<SIcon name="powered-by-flow-run-outlined" />
				</AnemicButton>
			</div>
		</div>
		<v-snackbar v-model="snackbar">
			{{ snackbarText }}
			<template v-slot:action="{ attrs }">
				<v-btn color="pink" text v-bind="attrs" @click="snackbar = false"> Close </v-btn>
			</template>
		</v-snackbar>
		<AppDialogManager v-if="!preventDialogs" />
		<div class="spinner-container-wrapper height-100">
			<div v-if="isShowingEndRun" class="flex-centerize">
				<EndRun :data="endRunData" @runWidget="handleWidgetNode" />
			</div>
			<template v-else-if="isLoading">
				<AppLoader v-if="!isFlowCanceled" class="spinner-container flex-centerize" />
				<Col class="flex-centerize mt-n12" v-else-if="isFlowCanceled">
					<h2 class="reg">Cancelled</h2>
				</Col>
			</template>
			<div v-else-if="runningDisabledFlow" class="flex-centerize">
				<div class="done-text">This flow is disabled and can not be run</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import { BackgroundMode, ProgressBarSettings } from '@ply-io/ply-front-shared-utilities';
import { FlowRunEvents, FlowRunStatuses } from '@ply-io/runner';

import EndRun from './EndRun';
import PortalAuth from './PortalAuth';
import { buildStatusLabel, handleDoneNodeRun, setTitle } from './flow-run-service';
import { ERROR_DATA_KEY } from './flow-run.const';
import store from '../../store';
import api, { getWorkflowRunProperties } from '@/api';
import { Modes } from '@/common.js';
import { BillingErrorCodes } from '@/components/Billing/billing-types';
import AppLoader from '@/components/common/AppLoader.vue';
import WorkflowStatus from '@/components/common/WorkflowStatus/WorkflowStatus.vue';
import AppDialogManager from '@/components/dialogs/AppDialogManager/AppDialogManager';
import { useRedirectToLogin } from '@/composables/redirect';
import { setIsFlowRun } from '@/store/modules/main/actions';
import { appURL } from '@/store/modules/main/getters';
import IntercomService from '@/utils/IntercomService';
import ErrorHandlingService, { ERROR_CODES } from '@/utils/error-handling-service';
import { isBackgroundColor, safeLog } from '@/utils/helpers.js';
import { MAPPED_FIELDS_KEY } from '@/utils/workflow-node.const';
import { WORKFLOW_ERROR_CODES } from '@/utils/workflow-service.const';
import { useRunner } from '@/views/SideBar/composables/runner';

const WidgetForm = () => import(/* webpackPrefetch: true */ '@/components/WidgetForm');
const isMobile = require('is-mobile');

const { redirectToLogin } = useRedirectToLogin();

const { runWorkflow } = useRunner(api.runnerApi);

const initialDataState = Object.freeze({
	snackbarText: '',
	snackbar: false,
	timeout: 5000,
	workflowMetadata: {},
	showIntro: true,
	showWidget: false,
	results: [],
	widgetValues: {},
	workflowNodesCount: 1,
	flowSettings: {},
	isFlowDone: false,
	isFlowCanceled: false,
	status: null,
	userIsOwner: false,
	userInOrganization: false,
	currentNodeRunningId: null,
	isLoading: false,
	runningDisabledFlow: false,
	endRunData: null,
	isPortalLogin: false,
	forceNonExistantToolError: false,
	didApplyProperties: false,
});

export default {
	name: 'FlowRun',
	props: {
		hashId: { type: String },
		resumeId: { type: String },
		flowVersionId: { type: String },
		inAppMode: { type: Boolean },
		preventDialogs: { type: Boolean, default: false },
		runMetadata: { type: Object, default: () => ({}) },
	},
	components: {
		WidgetForm,
		AppLoader,
		WorkflowStatus,
		AppDialogManager,
		PortalAuth,
		EndRun,
	},
	data: () => ({ ...initialDataState }),
	computed: {
		...mapGetters([appURL.fullPath]),
		workflowHashIdOrSlug() {
			return this.hashId || this.$route.path.replaceAll('/', '');
		},
		homeUrl() {
			return this[appURL.fullPath];
		},
		requestCodeText() {
			return this.flowSettings.customer_auth_text;
		},
		hideErrorsFromOrganization() {
			return this.flowSettings.should_hide_errors_from_organization_users;
		},
		hideErrorsFromNonOrganization() {
			return this.flowSettings.should_hide_errors_from_non_organization_users ?? true;
		},
		backgroundMode() {
			return this.flowSettings.background_mode || BackgroundMode.Off;
		},
		backgroundImageSrc() {
			if (this.backgroundMode === BackgroundMode.Image || this.flowSettings.mobile_background_enabled) {
				return this.flowSettings.background || this.flowSettings.background_file;
			} else {
				return this.flowSettings.background;
			}
		},
		hasMobileBackground() {
			return this.flowSettings.mobile_background_enabled;
		},
		isUploadedImage() {
			return this.backgroundImageSrc && (this.backgroundImageSrc.includes('.png') || this.backgroundImageSrc.includes('.jpg'));
		},
		backgroundImage() {
			if (!this.backgroundImageSrc || this.backgroundMode !== BackgroundMode.Image || isBackgroundColor(this.backgroundImageSrc)) {
				return '';
			}
			return this.isUploadedImage
				? 'url(' + this.backgroundImageSrc + ')'
				: 'url(' + require('../../assets/icons/' + this.backgroundImageSrc + '.png') + ')';
		},
		backgroundColor() {
			if (
				!this.backgroundImageSrc ||
				this.backgroundMode !== BackgroundMode.BackgroundColor ||
				!isBackgroundColor(this.backgroundImageSrc)
			) {
				return 'transparent';
			}
			return this.backgroundImageSrc;
		},
		progressBarMode() {
			return this.flowSettings.progress_bar || ProgressBarSettings.Simple;
		},
		showDebugger() {
			return this.progressBarMode === ProgressBarSettings.Regular;
		},
		showLoader() {
			return this.progressBarMode === ProgressBarSettings.Simple && this.status !== FlowRunStatuses.Failed;
		},
		isLogoActive() {
			return this.flowSettings.logo_enabled;
		},
		logoSrc() {
			return this.flowSettings.logo;
		},
		isMobile() {
			return isMobile();
		},
		isShowingEndRun() {
			return this.isFlowDone && !!this.endRunData;
		},
	},
	methods: {
		...mapActions([setIsFlowRun.fullPath]),
		applyWorkflowProperties() {
			if (this.flowSettings.favicon_enabled) {
				this.changeFavicon(this.flowSettings.favicon);
			}
			if (this.flowSettings.page_title_enabled) {
				document.title = this.flowSettings.page_title;
			}
			this.didApplyProperties = true;
		},
		async initRun() {
			if (IntercomService.loaded()) {
				IntercomService.shutdown();
			}
			safeLog(() => `FLOW_RUN: FlowRun component for Flow with hash id ${this.hashId} is mounted.`);

			this.$refs.workflowStatusRef.setState({
				isShow: true,
				text: 'Pending',
				percentCompleted: '0',
				runState: { status: FlowRunStatuses.Pending },
			});
			if (this.hashId) {
				try {
					this.flowSettings = await getWorkflowRunProperties(this.hashId);
					this.applyWorkflowProperties();
				} catch (e) {
					console.error('unable to apply workflow properties:', e);
				}
			}
		},
		async doRun() {
			try {
				const flowRun = await this.runWorkflow();

				try {
					if (!this.resumeId && this.workflowHashIdOrSlug) {
						await flowRun.run();
					} else if (this.resumeId && this.flowVersionId) {
						await flowRun.resumeNotif({ flowVersionId: this.flowVersionId, resumeId: this.resumeId });
					} else {
						// If nothing to show, lets show non existant tool error
						this.forceNonExistantToolError = true;
						this.setShowIntro(false);
						this.$refs.workflowStatusRef.setState({
							isShow: true,
						});
						this.status = FlowRunStatuses.Failed;
						// throw new Error('Invalid request: must start or resume a flow');
					}
				} catch (e) {
					if (e.code === WORKFLOW_ERROR_CODES.MISSING_PORTAL_AUTH) {
						this.isPortalLogin = true;
					} else if (e.code === ERROR_CODES.NOT_AUTHENTICATED || e.code === ERROR_CODES.TOKEN_INVALID) {
						redirectToLogin(this[appURL.fullPath]);
					} else if (ErrorHandlingService.extractErrorCode(e) === WORKFLOW_ERROR_CODES.RUNNING_DISABLED_FLOW) {
						this.runningDisabledFlow = true;
						this.setShowIntro(false);
					} else {
						throw e;
					}
				}

				if (flowRun.flowTitle && typeof flowRun.flowTitle === 'string') {
					this.$nextTick(() => setTitle(flowRun.flowTitle.trim()));
				}

				if (!this.didApplyProperties && flowRun.settings) {
					this.flowSettings = flowRun.settings;
					this.applyWorkflowProperties();
				}
			} catch (e) {
				console.error(e);
				this.setShowIntro(false);
				const isBillingError = Object.values(BillingErrorCodes).includes(e.code);
				this.$refs.workflowStatusRef.setState({
					errorParams: { isStepError: false, msg: e.message, code: e.code, forceShowError: !isBillingError },
				});
				this.status = FlowRunStatuses.Failed;
			}
		},
		showSnackbar(message, timeout = 5000) {
			this.snackbarText = message;
			this.snackbar = true;
			this.timeout = timeout;
		},
		setMetadata(workflowMetadata) {
			this.workflowMetadata = workflowMetadata;
		},
		setShowIntro(showIntro) {
			this.showIntro = showIntro;
		},
		setShowWidget(showWidget) {
			this.showWidget = showWidget;
		},
		setResults(results) {
			this.results = results;
		},
		setWorkflowNodesCount(workflowNodesCount) {
			this.workflowNodesCount = workflowNodesCount;
		},
		renderWidget(payload) {
			this.$nextTick(() => {
				this.setShowIntro(false);
				this.$refs.widget_layout.setWidget(payload);
			});
		},
		setWidgetValues(id, value) {
			this.$set(this.widgetValues, id, value);
		},

		async handlePortalAuthSucceeded() {
			this.isPortalLogin = false;
			this.doRun();
		},

		// --------- react to flow run updates -------------

		handleWidgetNode({ widgetId, widgetData, nodeRunIdPromise, submitCallback, cancelCallback }) {
			console.timeEnd('mounted->handleWidgetNode');
			if (IntercomService.loaded()) {
				IntercomService.shutdown();
			}
			this.setWidgetValues(null, null);
			this.renderWidget({
				widget: {
					...widgetData.output,
					widgetID: widgetId,
					nodeRunIdPromise,
					previousSteps: widgetData.previousSteps,
					webhookURL: null, // legacy,
					continueWebhookURL: null, // legacy,
				},
				callbacks: {
					valueUpdate: this.handleValueUpdate,
					doneClicked: async () => {
						this.setShowWidget(false);
						await submitCallback({ values: this.widgetValues });
					},
					cancelClicked: async () => {
						this.killFlowRun();
						await cancelCallback();
					},
					closeWidget: async () => {
						this.setShowWidget(false);
					},
					restartRun: async () => {
						this.killFlowRun();
						Object.assign(this.$data, { ...initialDataState });
						await this.initRun();
						this.doRun();
					},
				},
				api: { queryAC: api.queryAC, getWidgetFieldUploadUrl: api.getWidgetFieldUploadUrl },
			});
			this.setShowWidget(true);
		},

		handleValueUpdate(data) {
			let { value } = data;
			if (value?.value) {
				// Hack
				value = value.value;
			}
			this.setWidgetValues(data.id, value);
		},

		async handleFlowRunEnded(payload) {
			this.setShowIntro(false);
			this.isFlowDone = true;
			const data = (await payload.data) || {};
			this.endRunData = data;
			this.$emit('onFlowEnded', data);
		},

		killFlowRun() {
			safeLog('FLOW_RUN: killFlowRun called');
			this.setShowWidget(false);
			this.isFlowCanceled = true;
		},

		transformStateForWorkflowStatus(state) {
			this.status = state.status;
			const nodeRuns = Object.values(state.nodeRuns);
			const statusLabel = buildStatusLabel(this.status);

			return {
				isShow: true,
				text: statusLabel,
				percentCompleted: (state.progress * 100).toString(),
				runState: {
					nodeRuns,
					status: this.status,
				},
			};
		},
		changeFavicon(newFaviconUrl) {
			if (newFaviconUrl) {
				var link = document.querySelector("link[rel~='icon']");
				if (!link) {
					link = document.createElement('link');
					link.rel = 'icon';
					document.getElementsByTagName('head')[0].appendChild(link);
				}
				link.href = newFaviconUrl;
			}
		},

		async runWorkflow() {
			const urlParameters = Object.fromEntries(new URLSearchParams(window.location.search).entries());
			const input = {
				[MAPPED_FIELDS_KEY]: { url_parameters: urlParameters, ...urlParameters },
				domain: process.env.NODE_ENV === Modes.Production ? window.location.hostname : null,
				...this.runMetadata,
			};

			const flowRun = await runWorkflow(this.workflowHashIdOrSlug, input, {
				[FlowRunEvents.StateChange]: (state) => {
					safeLog(() => {
						const serviceData = Object.keys(state.nodeRuns).map((a) => ({
							serviceSlug: state.nodeRuns[a].serviceSlug,
							action: state.nodeRuns[a].action,
						}));
						return 'FLOW_RUN: State change: serviceData: ' + JSON.stringify(serviceData);
					});
					console.log('State change:', state);
					this.status = state.status;
					let additionalParams = {};
					if (state.status === FlowRunStatuses.Failed) {
						let editStepUrl = null;
						let creatorWasNotified = null;
						const nodeId = flowRun.failedNodeRun?.data?.output[ERROR_DATA_KEY]?.nodeId;
						const projectId = flowRun.failedNodeRun?.data?.output[ERROR_DATA_KEY]?.projectId;
						this.userIsOwner = flowRun.userIsOwner;
						this.userInOrganization = flowRun.userInOrganization;
						if (flowRun.userIsOwner && flowRun.flowId && nodeId && projectId) {
							editStepUrl = store.getters['nodes/editNodeLink']({ projectId, flowId: flowRun.flowId, nodeId });
						}
						if (!flowRun.userIsOwner) {
							creatorWasNotified = 'The module creator was notified';
						}

						additionalParams = {
							errorParams: {
								isStepError: Boolean(flowRun.failedNodeRun),
								msg: flowRun.errorMsg,
								actionTitle: flowRun.failedNodeRun?.actionTitle,
								serviceSlug: flowRun.failedNodeRun?.serviceSlug,
								editStepUrl,
								creatorWasNotified,
							},
						};
					}
					this.$refs.workflowStatusRef.setState({ ...this.transformStateForWorkflowStatus(state), ...additionalParams });
					const currentNodeRunningId = Object.keys(state.nodeRuns).find((id) => state.nodeRuns[id].status === 'waiting');
					if (this.currentNodeRunningId !== currentNodeRunningId) {
						this.$set(this, 'isLoading', [null, undefined].includes(this.currentNodeRunningId) ? false : true);
						this.$set(this, 'currentNodeRunningId', currentNodeRunningId);
					}
				},
				[FlowRunEvents.Failed]: () => this.setShowIntro(false),
				[FlowRunEvents.Done]: this.handleFlowRunEnded,
				[FlowRunEvents.RenderableWidget]: (eventData) => {
					this.handleWidgetNode(eventData);
					this.$set(this, 'isLoading', false);
				},
				[FlowRunEvents.NodeRunDone]: (nodeRunState) => handleDoneNodeRun(nodeRunState),
			});

			return flowRun;
		},
	},

	created() {
		this[setIsFlowRun.fullPath](true);
	},

	async mounted() {
		console.timeEnd('entrypoint->mounted');
		console.time('mounted->handleWidgetNode');
		console.time('mounted->runResponseArrived');

		await this.initRun();
		this.doRun();
	},

	watch: {
		status: {
			handler(newV) {
				this.$emit('status-changed', newV);
			},
		},
	},
};
</script>

<style scoped lang="scss">
@import '@/components/WidgetForm/widget-form-mixins.scss';

.flow-run {
	position: relative;
	height: 100vh;

	.flow-title {
		margin-top: 10px;
		text-align: center;
		font-size: 20px;
		color: #292829;
		font-weight: 500;
	}
	.flow-content {
		width: 100%;
		height: 100%;
	}
	&.in-app-mode {
		@media (min-width: #{$device-sm + 1px}) {
			.workflow-status-wrapper {
				bottom: 16px !important;
				width: unset !important;
			}
			::v-deep(.widget-form) {
				padding-top: 44px !important;
			}
			::v-deep(.widget-form-element) {
				border: 1px solid #dfe4ef !important;
				box-shadow: 0px 3px 6px var(--shadow-color) !important;
			}
		}
	}
	::v-deep {
		.widget-form {
			display: flex;
		}
		.v-footer {
			width: 100%;
		}
	}
	.loader-container {
		position: absolute;
		top: 0;
		left: 0;
		height: 100vh;
		width: 100vw;
	}

	.workflow-status-wrapper {
		position: fixed;
		bottom: 4px;
	}

	.workflow-status {
		position: static;
	}
	.progress_area {
		display: block !important;
	}
	.powered-by-button {
		padding: 0px;
		width: 104px;
		height: 32px !important;
	}
	.spinner-container {
		width: 40px;
		height: 40px;
		border-radius: 6px;
		background: var(--bg-color);
	}

	/* Responsive */

	.mobile-margin {
		margin-right: 100px;
	}

	.spinner-container-wrapper {
		font-weight: 600;
	}

	@media (max-width: $device-sm) {
		padding: 8px 8px var(--widget-field-mobile-bottom-pad) 8px;

		::v-deep(.widget-form-element) {
			@include widget-form-tablet-layout();

			background-color: transparent !important; // background determined elsewhere
		}

		.powered-by-button {
			display: none;
		}

		.workflow-status-wrapper {
			padding: 4px !important;
			margin: 0 !important;

			::v-deep(.workflow-status) {
				width: 100%;

				.actions-list,
				.progress_wrp {
					width: 100%;
				}
			}
		}

		::v-deep(.widget-form) {
			.buttons-section {
				position: fixed;
				bottom: 0;
				left: 0;
				width: 100vw;
				padding: 10px 5px 65px 0;
				border-top: var(--primary-border);
				background: #ffffff99;

				.buttons-section-container {
					.buttons-container {
						display: flex;
						align-items: space-between;
						justify-content: center;
						width: 100%;

						.cancel-button {
							background-color: #f5f7fa !important;
						}
						button {
							width: 40%;
						}
					}
				}
			}
		}

		&:not(.has-mobile-background) {
			background-image: none !important;
		}
	}
	@media (min-width: #{$device-sm + 1px}) {
		.flow-content {
			padding: 30px 0 30px 0;
		}

		::v-deep(.widget-form-element) {
			@include widget-form-desktop-layout(200px);
		}
	}
}
</style>
